import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';

import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import DepartmentSettings from './DepartmentSettings';
import EmailTemplateSettings from './EmailTemplateSettings';
import TeamSettings from './TeamSettings';
import ProfileSettings from './ProfileSettings';
import DemoAccountBanner from 'components/DemoAccountBanner/DemoAccountBanner';
import { Button } from 'components/Button/RoundButton';
import { Container, Wrapper } from 'components/Layout/Layout';
import AnchorContent from 'components/AnchorContent/AnchorContent';
import { TopButtonsContainer, TopHeadingContainer, Header, AnchorLink } from './styles';
import { getConfig } from 'functions/getConfig';
import { Spinner } from 'components/Spinner/Spinner';
import YesNoModal from 'components/Modals/yesNoModal';
import { userRoles } from 'constants/users';

export default React.memo(() => {
  const config = getConfig();
  let { departmentId } = useParams();
  const navigate = useNavigate();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});
  const [isDefaultDepartment, setIsDefaultDepartment] = useState(false);

  const { loadDepartmentSettings, deleteDepartment, resetUpdatedIds } = useActions(actions);

  let { fetchSettings, error, settings, updatedDepartmentId, isDepartmentDeleted } = useSelector(
    (state) => state.SettingsPageReducer
  );

  let { userRole } = useSelector((state) => state.auth);

  useEffect(() => {
    if (error && !error.includes('internal error')) toast.error(error);
  }, [error]);

  useEffect(() => {
    loadDepartmentSettings(departmentId);
  }, [loadDepartmentSettings, departmentId]);

  useEffect(() => {
    if (updatedDepartmentId) {
      toast.success('Department updated successfully');
      resetUpdatedIds();
    }
  }, [updatedDepartmentId, resetUpdatedIds]);

  useEffect(() => {
    if (isDepartmentDeleted) {
      toast.success('Department deleted successfully');
      navigate('/settings');
    }
  }, [isDepartmentDeleted, navigate]);

  useEffect(() => {
    if (settings?.departments?.[0]) setIsDefaultDepartment(settings.departments[0].default);
  }, [settings]);

  let profileSettingsAnchorPoint = [
    {
      label: 'Profile',
      component: userRole && <ProfileSettings />,
      icon: ' fa fa-user',
    },
  ];

  let anchorPoints = [
    {
      label: 'Department',
      component: settings.departments && <DepartmentSettings isDepartmentSettingsPage />,
      icon: 'fa fa-building',
    },
    {
      label: 'Team',
      component: settings.teamMembers && <TeamSettings isDepartmentSettingsPage />,
      icon: 'fa-solid fa-user-group',
    },
    {
      label: 'Email Template',
      component: settings.emails && <EmailTemplateSettings />,
      icon: 'fa fa-envelope',
    },
  ];

  if (userRole && userRole === userRoles.DEPARTMENT_ADMIN) anchorPoints.splice(0, 0, ...profileSettingsAnchorPoint);

  const confirmModal = (isConfirmed) => {
    setShowConfirmationModal(false);
    if (isConfirmed) deleteDepartment(departmentId);
    setModalInfo({});
  };

  return (
    <Wrapper padding="25px 0 0 0" minHeight="100vh">
      {showConfirmationModal && (
        <YesNoModal modalInfo={modalInfo} confirm={(isConfirmed) => confirmModal(isConfirmed)} />
      )}
      <Container column>
        <TopHeadingContainer>
          <Header>Department Settings</Header>
          <TopButtonsContainer>
            <AnchorLink href={`${config.REDIRECTION_URL}/gdpr-candidates`}>
              <Button primaryBlue primaryGreenBorder removeMargin>
                Candidates to be deleted
              </Button>
            </AnchorLink>
            {[userRoles.SUPER_ADMIN, userRoles.COMPANY_ADMIN, userRoles.RESELLER].includes(userRole) &&
              !isDefaultDepartment && (
                <Button
                  primaryRed
                  removeMargin
                  margin="0 0 0 10px"
                  onClick={() => {
                    setShowConfirmationModal(true);
                    setModalInfo({
                      title: 'Confirmation',
                      body: 'Do you want to delete the department?',
                    });
                  }}
                >
                  Delete
                </Button>
              )}
          </TopButtonsContainer>
        </TopHeadingContainer>
        {settings?.company?.freeDemo && <DemoAccountBanner userRole={userRole} />}
        {fetchSettings && <Spinner />}
        <AnchorContent anchorPoints={anchorPoints} hideOnSmallDevice={true}></AnchorContent>
      </Container>
    </Wrapper>
  );
});
